import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, X, Calendar, MapPin, LayoutGrid, Table as TableIcon, Download, ArrowUpDown, Search } from "lucide-react";
import { format, parseISO } from "date-fns";
import { Pagination } from "@/components/Pagination";
import { PhoneInput } from "@/components/PhoneInput";

function formatDate(d) {
  if (!d) return "—";
  try { return format(parseISO(d), "dd MMM yyyy"); } catch { return d; }
}

function formatDateShort(d) {
  if (!d) return "";
  try { return format(parseISO(d), "dd MMM"); } catch { return d; }
}

function formatDateRange(start, end) {
  if (!start && !end) return "—";
  if (!end || start === end) return formatDate(start);
  try {
    const s = parseISO(start);
    const e = parseISO(end);
    if (s.getFullYear() === e.getFullYear()) {
      return `${format(s, "dd MMM")} – ${format(e, "dd MMM yyyy")}`;
    }
    return `${formatDate(start)} – ${formatDate(end)}`;
  } catch { return `${start} – ${end}`; }
}

function INR(n, currency = "INR") {
  try { return new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format(n || 0); }
  catch { return `${currency} ${n}`; }
}

const STATUS_STYLES = {
  confirmed: "bg-[#4E9A6B]/12 text-[#357350] border-[#4E9A6B]/40",
  soft_block: "bg-[#E4B62C]/20 text-[#7a5f0c] border-[#E4B62C]/55",
  pending: "bg-[#D4A373]/10 text-[#D4A373] border-[#D4A373]/30",
  lost: "bg-red-50 text-red-700 border-red-200",
};
const STATUS_LABEL = { pending: "Pending", confirmed: "Confirmed", soft_block: "Soft Block", lost: "Lost", all: "All" };

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const SORT_LABEL = { upcoming: "Upcoming first", asc: "Oldest first", desc: "Newest first" };
const nextSort = (d) => (d === "upcoming" ? "asc" : d === "asc" ? "desc" : "upcoming");
const YEAR_NOW = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => YEAR_NOW - 1 + i);

function StatusBadge({ status }) {
  return (
    <span className={`px-3 py-1 text-[10px] uppercase tracking-[0.25em] border rounded-full ${STATUS_STYLES[status] || STATUS_STYLES.pending}`}>
      {STATUS_LABEL[status] || "Pending"}
    </span>
  );
}

function NewWeddingModal({ open, onClose, onSaved }) {
  const initial = {
    couple_names: "", start_date: "", end_date: "", location: "", total_price: 0, currency: "INR",
    status: "pending", lead_source: "", contact_phone: "", instagram_id: "", notes: "",
  };
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(initial); /* eslint-disable-next-line */ }, [open]);
  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if ((form.contact_phone || "").replace(/\D/g, "").length < 10) { toast.error("Enter a valid contact phone"); return; }
    setSaving(true);
    try {
      const payload = { ...form, total_price: Number(form.total_price) || 0 };
      if (!payload.end_date) payload.end_date = payload.start_date;
      await api.post("/weddings", payload);
      toast.success("Lead added");
      onSaved(); onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="wedding-form-modal">
      <div className="bg-white border border-[#EAE7E0] max-w-lg w-full my-8">
        <div className="flex items-center justify-between p-6 border-b border-[#EAE7E0]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652]">New Lead</div>
            <h2 className="font-serif text-3xl tracking-tight">Wedding</h2>
          </div>
          <button onClick={onClose} className="text-[#5C5652] hover:text-[#2A2421]" data-testid="close-wedding-form"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Couple names *</label>
            <input required value={form.couple_names} onChange={(e) => setForm({ ...form, couple_names: e.target.value })} data-testid="wedding-couple-input"
              className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" placeholder="Sarah & James" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Start date</label>
              <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="wedding-start-date-input"
                className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">End date</label>
              <input type="date" value={form.end_date} min={form.start_date || undefined} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="wedding-end-date-input"
                className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Status *</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="wedding-status-select"
                className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none">
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="soft_block">Soft Block</option>
                <option value="lost">Lost</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Total ({form.currency})</label>
              <input type="number" min="0" value={form.total_price} onChange={(e) => setForm({ ...form, total_price: e.target.value })} data-testid="wedding-price-input"
                className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" />
            </div>
            <div className="col-span-2">
              <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Lead source</label>
              <input value={form.lead_source} onChange={(e) => setForm({ ...form, lead_source: e.target.value })} data-testid="wedding-source-input"
                className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" placeholder="Instagram, referral…" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Primary location</label>
            <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="wedding-location-input"
              className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" placeholder="Taj Palace, Delhi" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Contact phone *</label>
              <PhoneInput value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} testId="wedding-phone-input" variant="line" placeholder="98…" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Instagram ID</label>
              <input value={form.instagram_id} onChange={(e) => setForm({ ...form, instagram_id: e.target.value.replace(/^@/, "") })} data-testid="wedding-instagram-input"
                className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" placeholder="@handle" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Notes</label>
            <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="wedding-notes-input"
              className="w-full border border-[#EAE7E0] bg-transparent p-3 focus:border-[#C88270] focus:outline-none" />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-[#EAE7E0]">
            <button type="button" onClick={onClose} className="px-6 py-2 text-xs uppercase tracking-[0.2em] text-[#5C5652]">Cancel</button>
            <button type="submit" disabled={saving} data-testid="wedding-submit-button"
              className="px-6 py-2 bg-[#C88270] text-white text-xs uppercase tracking-[0.25em] hover:bg-[#B37363] transition-colors disabled:opacity-50">
              {saving ? "Saving…" : "Add lead"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function exportCSV(rows) {
  const headers = ["Start date", "End date", "Couple", "Status", "Location", "Sub-events", "Total (INR)", "Lead source", "Contact phone", "Instagram"];
  const escape = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers.join(",")].concat(
    rows.map((w) => [
      w.start_date || w.primary_date || "",
      w.end_date || w.start_date || w.primary_date || "",
      w.couple_names, w.status || "pending", w.location || "", w.event_count || 0, w.total_price || 0, w.lead_source || "", w.contact_phone || "", w.instagram_id ? `@${w.instagram_id}` : ""
    ].map(escape).join(","))
  ).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `weddings-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
}

export default function WeddingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [weddings, setWeddings] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [view, setView] = useState("table");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortDir, setSortDir] = useState("upcoming");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const PAGE_SIZE = 15;
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("new") === "1" && isAdmin) {
      setModal(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isAdmin]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [statusFilter, debouncedSearch, sortDir, month, year]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: PAGE_SIZE, status: statusFilter, search: debouncedSearch, sort: sortDir };
      if (month) params.month = month;
      if (year) params.year = year;
      const { data } = await api.get("/weddings", { params });
      setWeddings(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      setCounts(data.counts || {});
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [page, statusFilter, debouncedSearch, sortDir, month, year]);
  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/weddings/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const exportAll = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/weddings");
      const q = debouncedSearch.trim().toLowerCase();
      const rows = (data || []).filter((w) => {
        if (statusFilter !== "all" && (w.status || "pending") !== statusFilter) return false;
        if (!q) return true;
        return [w.couple_names, w.location, w.lead_source, w.contact_phone, w.instagram_id]
          .some((f) => (f || "").toString().toLowerCase().includes(q));
      });
      exportCSV(rows);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setExporting(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-10 md:py-16" data-testid="weddings-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652] mb-3">Studio · Leads</div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-[#2A2421]">Weddings</h1>
          <p className="text-sm text-[#5C5652] mt-2 max-w-lg">Every new inquiry is added as a lead. Mark it <em>confirmed</em> once booked, then add sub-events like Mehendi, Sangeet and the Wedding day.</p>
        </div>
        {isAdmin && (
          <button onClick={() => setModal(true)} data-testid="new-wedding-button"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#C88270] text-white text-xs uppercase tracking-[0.25em] hover:bg-[#B37363] transition-colors self-start">
            <Plus size={16} /> New Lead
          </button>
        )}
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search size={16} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#5C5652]" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="weddings-search-input"
          placeholder="Search by couple, location, phone, Instagram or source…"
          className="w-full pl-7 pr-3 py-2 border-b border-[#EAE7E0] bg-transparent focus:border-[#C88270] focus:outline-none text-sm" />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6 pb-4 border-b border-[#EAE7E0]">
        <div className="flex gap-6">
          {["all", "pending", "confirmed", "soft_block", "lost"].map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)} data-testid={`status-filter-${s}`}
              className={`text-xs uppercase tracking-[0.25em] pb-1 transition-colors ${statusFilter === s ? "text-[#C88270] border-b-2 border-[#C88270]" : "text-[#5C5652] hover:text-[#2A2421]"}`}>
              {STATUS_LABEL[s]} <span className="ml-1 text-[#5C5652]/60">({counts[s] || 0})</span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportAll} disabled={exporting} data-testid="export-csv-button"
            className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.2em] border border-[#EAE7E0] text-[#5C5652] hover:border-[#2A2421] hover:text-[#2A2421] transition-colors disabled:opacity-50">
            <Download size={14} /> {exporting ? "…" : "CSV"}
          </button>
          <div className="flex border border-[#EAE7E0]">
            <button onClick={() => setView("table")} data-testid="view-table"
              className={`p-2 ${view === "table" ? "bg-[#2A2421] text-white" : "text-[#5C5652]"}`} title="Table view"><TableIcon size={16} /></button>
            <button onClick={() => setView("cards")} data-testid="view-cards"
              className={`p-2 ${view === "cards" ? "bg-[#2A2421] text-white" : "text-[#5C5652]"}`} title="Card view"><LayoutGrid size={16} /></button>
          </div>
        </div>
      </div>

      {/* Month / Year + sort filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <span className="text-[10px] uppercase tracking-[0.25em] text-[#5C5652]">Filter by date</span>
        <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="filter-month"
          className="border border-[#EAE7E0] bg-white text-sm text-[#2A2421] px-3 py-2 focus:border-[#C88270] focus:outline-none cursor-pointer">
          <option value="">All months</option>
          {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
        <select value={year} onChange={(e) => setYear(e.target.value)} data-testid="filter-year"
          className="border border-[#EAE7E0] bg-white text-sm text-[#2A2421] px-3 py-2 focus:border-[#C88270] focus:outline-none cursor-pointer">
          <option value="">All years</option>
          {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        {(month || year) && (
          <button onClick={() => { setMonth(""); setYear(""); }} data-testid="filter-date-clear"
            className="text-[10px] uppercase tracking-[0.2em] text-[#C88270] hover:text-[#2A2421]">Clear</button>
        )}
        <button onClick={() => setSortDir(nextSort(sortDir))} data-testid="sort-toggle"
          className="ml-auto inline-flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.2em] border border-[#EAE7E0] text-[#5C5652] hover:border-[#2A2421] hover:text-[#2A2421] transition-colors">
          <ArrowUpDown size={12} /> {SORT_LABEL[sortDir]}
        </button>
      </div>

      {loading ? (
        <div className="text-xs uppercase tracking-[0.3em] text-[#5C5652]">Loading…</div>
      ) : total === 0 ? (
        <div className="p-12 bg-white border border-[#EAE7E0] text-center text-[#5C5652]">No leads match.</div>
      ) : view === "table" ? (
        <div className="hidden md:block bg-white border border-[#EAE7E0] overflow-x-auto" data-testid="weddings-table">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-[#EAE7E0]">
                <th className="py-4 px-6 text-[10px] uppercase tracking-[0.25em] text-[#5C5652]">
                  <button onClick={() => setSortDir(nextSort(sortDir))} className="inline-flex items-center gap-2 hover:text-[#2A2421]" data-testid="sort-by-date">
                    Dates <ArrowUpDown size={12} />
                  </button>
                </th>
                <th className="py-4 px-6 text-[10px] uppercase tracking-[0.25em] text-[#5C5652]">Couple</th>
                <th className="py-4 px-6 text-[10px] uppercase tracking-[0.25em] text-[#5C5652]">Status</th>
                <th className="py-4 px-6 text-[10px] uppercase tracking-[0.25em] text-[#5C5652]">Location</th>
                <th className="py-4 px-6 text-[10px] uppercase tracking-[0.25em] text-[#5C5652] text-right">Sub-events</th>
                {isAdmin && <th className="py-4 px-6 text-[10px] uppercase tracking-[0.25em] text-[#5C5652] text-right">Total</th>}
                <th className="py-4 px-6"></th>
              </tr>
            </thead>
            <tbody>
              {weddings.map((w) => (
                <tr key={w.id} className="border-b border-[#EAE7E0] last:border-0 hover:bg-[#FAF9F6]/60" data-testid={`wedding-row-${w.id}`}>
                  <td className="py-4 px-6 text-sm text-[#2A2421] whitespace-nowrap">{formatDateRange(w.start_date || w.primary_date, w.end_date)}</td>
                  <td className="py-4 px-6">
                    <Link to={`/weddings/${w.id}`} className="text-sm font-medium text-[#2A2421] hover:text-[#C88270]" data-testid={`wedding-link-${w.id}`}>{w.couple_names}</Link>
                    {w.lead_source && <div className="text-xs text-[#5C5652]">via {w.lead_source}</div>}
                  </td>
                  <td className="py-4 px-6">
                    {isAdmin ? (
                      <select value={w.status || "pending"} onChange={(e) => changeStatus(w.id, e.target.value)} data-testid={`row-status-${w.id}`}
                        className={`text-[10px] uppercase tracking-[0.25em] border rounded-full px-3 py-1 bg-transparent cursor-pointer ${STATUS_STYLES[w.status || "pending"]}`}>
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="soft_block">Soft Block</option>
                        <option value="lost">Lost</option>
                      </select>
                    ) : <StatusBadge status={w.status} />}
                  </td>
                  <td className="py-4 px-6 text-sm text-[#5C5652]">{w.location || "—"}</td>
                  <td className="py-4 px-6 text-sm text-[#2A2421] text-right">{w.event_count || 0}</td>
                  {isAdmin && <td className="py-4 px-6 text-sm text-[#2A2421] text-right whitespace-nowrap">{INR(w.total_price, w.currency)}</td>}
                  <td className="py-4 px-6 text-right">
                    <Link to={`/weddings/${w.id}`} className="text-[10px] uppercase tracking-[0.25em] text-[#C88270] hover:text-[#B37363]" data-testid={`open-wedding-${w.id}`}>Open →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {weddings.map((w) => (
            <Link key={w.id} to={`/weddings/${w.id}`} data-testid={`wedding-card-${w.id}`}
              className="p-6 bg-white border border-[#EAE7E0] hover:border-[#C88270]/60 transition-colors group">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#C88270]">Wedding</div>
                <StatusBadge status={w.status} />
              </div>
              <div className="font-serif text-3xl tracking-tight text-[#2A2421] group-hover:text-[#C88270] transition-colors">{w.couple_names}</div>
              <div className="space-y-2 text-xs text-[#5C5652] mt-6 border-t border-[#EAE7E0] pt-4">
                <div className="flex items-center gap-2"><Calendar size={14} strokeWidth={1.5} /> {formatDateRange(w.start_date || w.primary_date, w.end_date)}</div>
                {w.location && <div className="flex items-center gap-2"><MapPin size={14} strokeWidth={1.5} /> {w.location}</div>}
                <div className="flex items-center justify-between pt-1"><span>{w.event_count || 0} sub-event{w.event_count === 1 ? "" : "s"}</span>{w.next_event_date && <span>Next {formatDateShort(w.next_event_date)}</span>}</div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {!loading && total > 0 && (
        <div className="md:hidden space-y-4" data-testid="weddings-mobile-list">
          {weddings.map((w) => (
            <div key={w.id} data-testid={`wedding-mcard-${w.id}`}
              className="bg-white border border-[#EAE7E0] rounded-[20px] p-5 shadow-[0_2px_12px_-4px_rgba(42,36,33,0.05)] transition-transform active:scale-[0.99]">
              <div className="flex items-start justify-between gap-3">
                <Link to={`/weddings/${w.id}`} className="font-serif text-[22px] leading-tight text-[#2A2421] line-clamp-2">{w.couple_names}</Link>
                <StatusBadge status={w.status} />
              </div>
              <div className="mt-2 space-y-1 text-[13px] text-[#5C5652]">
                <div className="flex items-center gap-2"><Calendar size={14} strokeWidth={1.5} /> {formatDateRange(w.start_date || w.primary_date, w.end_date)}</div>
                {w.location && <div className="flex items-center gap-2"><MapPin size={14} strokeWidth={1.5} /> {w.location}</div>}
                {w.lead_source && <div className="text-[12px] text-[#B8B2AA]">via {w.lead_source}</div>}
              </div>
              {isAdmin && (
                <select value={w.status || "pending"} onChange={(e) => changeStatus(w.id, e.target.value)} data-testid={`mrow-status-${w.id}`}
                  className={`mt-3 w-full min-h-[44px] rounded-xl border px-3 text-sm bg-white ${STATUS_STYLES[w.status || "pending"]}`}>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="soft_block">Soft Block</option>
                  <option value="lost">Lost</option>
                </select>
              )}
              <div className="mt-3 pt-3 border-t border-[#EAE7E0]/60 flex items-center justify-between">
                <span className="text-[13px] text-[#5C5652]">{w.event_count || 0} sub-event{w.event_count === 1 ? "" : "s"}{isAdmin ? ` · ${INR(w.total_price, w.currency)}` : ""}</span>
                <Link to={`/weddings/${w.id}`} data-testid={`mopen-wedding-${w.id}`} className="text-[11px] uppercase tracking-[0.2em] text-[#C88270] font-semibold">Open →</Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && total > 0 && (
        <Pagination page={page} totalPages={totalPages} total={total} pageSize={PAGE_SIZE} onChange={setPage} testId="weddings-pagination" />
      )}

      <NewWeddingModal open={modal} onClose={() => setModal(false)} onSaved={load} />
    </div>
  );
}
